import { useEffect, useState, type FormEvent } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { categories } from "@/mocks/categories";
import { themeOf } from "@/utils/content";

const navItems = [
  { to: "/", label: "首页" },
  { to: "/category/all", label: "全部爆料" },
  { to: "/faq", label: "常见问答" },
  { to: "/about", label: "关于我们" },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [catOpen, setCatOpen] = useState(false);
  const [keyword, setKeyword] = useState("");
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setMenuOpen(false);
    setCatOpen(false);
  }, [location.pathname]);

  const light = !scrolled && !menuOpen;

  const handleSearch = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const kw = keyword.trim();
    navigate(kw ? `/category/all?q=${encodeURIComponent(kw)}` : "/category/all");
    setMenuOpen(false);
  };

  const isActive = (to: string) => {
    if (to === "/") return location.pathname === "/";
    if (to === "/category/all") return location.pathname === "/category/all";
    return location.pathname.startsWith(to);
  };

  return (
    <header
      className={`fixed inset-x-0 top-0 z-50 w-full transition-all duration-300 ${
        light
          ? "bg-transparent"
          : "border-b border-background-200 bg-background-50/95 backdrop-blur-md"
      }`}
    >
      <nav className="flex w-full items-center justify-between gap-4 px-4 py-3 md:px-8">
        <Link to="/" className="flex shrink-0 items-center gap-2" aria-label="511爆料首页">
          <span className="flex h-9 w-9 items-center justify-center rounded-md bg-primary-500 font-brand text-[15px] leading-none text-background-50">
            511
          </span>
          <span className={`font-heading text-lg font-bold tracking-tight ${light ? "text-background-50" : "text-foreground-950"}`}>
            511爆料
          </span>
        </Link>

        <div className="hidden items-center gap-1 lg:flex">
          {navItems.map((item) => (
            <Link
              key={item.to}
              to={item.to}
              className={`whitespace-nowrap rounded-md px-3 py-2 text-sm font-medium transition-colors ${
                light
                  ? "text-background-50/90 hover:bg-background-50/15 hover:text-background-50"
                  : "text-foreground-700 hover:bg-background-100 hover:text-foreground-950"
              } ${isActive(item.to) ? (light ? "text-background-50" : "text-primary-600") : ""}`}
            >
              {item.label}
            </Link>
          ))}

          <div
            className="relative"
            onMouseEnter={() => setCatOpen(true)}
            onMouseLeave={() => setCatOpen(false)}
          >
            <button
              type="button"
              className={`flex items-center gap-1 whitespace-nowrap rounded-md px-3 py-2 text-sm font-medium transition-colors ${
                light
                  ? "text-background-50/90 hover:bg-background-50/15 hover:text-background-50"
                  : "text-foreground-700 hover:bg-background-100 hover:text-foreground-950"
              }`}
              aria-expanded={catOpen}
            >
              分类导航
              <i className={`ri-arrow-down-s-line text-base transition-transform ${catOpen ? "rotate-180" : ""}`} />
            </button>

            {catOpen && (
              <div className="absolute left-0 top-full w-72 pt-2">
                <div className="rounded-lg border border-background-200 bg-background-50 p-2 shadow-sm">
                  {categories.map((item) => {
                    const theme = themeOf(item.slug);
                    return (
                      <Link
                        key={item.slug}
                        to={`/category/${item.slug}`}
                        className="flex items-start gap-3 rounded-md px-3 py-2.5 transition-colors hover:bg-background-100"
                      >
                        <span className={`mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-md ${theme.chip}`}>
                          <i className={item.icon} />
                        </span>
                        <span className="min-w-0">
                          <span className="flex items-center gap-2 text-sm font-semibold text-foreground-950">
                            {item.name}
                            <span className="text-xs font-normal text-foreground-500">{item.updated}</span>
                          </span>
                          <span className="mt-0.5 block truncate text-xs text-foreground-600">{item.tagline}</span>
                        </span>
                      </Link>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        </div>

        <div className="flex items-center gap-2">
          <form onSubmit={handleSearch} className="hidden items-center md:flex">
            <div
              className={`flex items-center gap-2 rounded-full border px-3 py-1.5 transition-colors ${
                light ? "border-background-50/40 bg-background-950/20" : "border-background-300 bg-background-100"
              }`}
            >
              <span className={`flex h-5 w-5 items-center justify-center ${light ? "text-background-50/80" : "text-foreground-500"}`}>
                <i className="ri-search-line text-base" />
              </span>
              <input
                value={keyword}
                onChange={(event) => setKeyword(event.target.value)}
                placeholder="搜索爆料关键词"
                className={`w-32 bg-transparent text-sm outline-none transition-all focus:w-44 lg:w-40 lg:focus:w-52 ${
                  light ? "text-background-50 placeholder:text-background-50/60" : "text-foreground-900 placeholder:text-foreground-500"
                }`}
                aria-label="搜索爆料关键词"
              />
            </div>
          </form>

          <Link
            to="/category/all"
            className="hidden whitespace-nowrap rounded-md bg-primary-500 px-4 py-2 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600 md:inline-flex"
          >
            随便看看
          </Link>

          <button
            type="button"
            onClick={() => setMenuOpen((open) => !open)}
            className={`flex h-9 w-9 items-center justify-center rounded-md text-xl lg:hidden ${
              light ? "text-background-50" : "text-foreground-900"
            }`}
            aria-label="展开导航菜单"
            aria-expanded={menuOpen}
          >
            <i className={menuOpen ? "ri-close-line" : "ri-menu-line"} />
          </button>
        </div>
      </nav>

      {menuOpen && (
        <div className="border-t border-background-200 bg-background-50 px-4 pb-6 pt-4 lg:hidden">
          <form onSubmit={handleSearch} className="mb-4">
            <div className="flex items-center gap-2 rounded-md border border-background-300 bg-background-100 px-3 py-2">
              <span className="flex h-5 w-5 items-center justify-center text-foreground-500">
                <i className="ri-search-line text-base" />
              </span>
              <input
                value={keyword}
                onChange={(event) => setKeyword(event.target.value)}
                placeholder="搜索爆料关键词"
                className="w-full bg-transparent text-sm text-foreground-900 outline-none placeholder:text-foreground-500"
                aria-label="搜索爆料关键词"
              />
            </div>
          </form>

          <div className="flex flex-col">
            {navItems.map((item) => (
              <Link
                key={item.to}
                to={item.to}
                className="whitespace-nowrap rounded-md px-3 py-3 text-sm font-medium text-foreground-800 transition-colors hover:bg-background-100"
              >
                {item.label}
              </Link>
            ))}
          </div>

          <p className="mt-4 px-3 text-xs font-semibold uppercase tracking-wider text-foreground-500">分类快速直达</p>
          <div className="mt-2 grid grid-cols-2 gap-2">
            {categories.map((item) => {
              const theme = themeOf(item.slug);
              return (
                <Link
                  key={item.slug}
                  to={`/category/${item.slug}`}
                  className="flex items-center gap-2 rounded-md border border-background-200 px-3 py-2 text-sm text-foreground-800"
                >
                  <span className={`flex h-5 w-5 items-center justify-center rounded text-xs ${theme.chip}`}>
                    <i className={item.icon} />
                  </span>
                  {item.short}
                </Link>
              );
            })}
          </div>
        </div>
      )}
    </header>
  );
}